import { SvgChevronright } from '@common/icon';
import { cn } from '@core/utils';

import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';

import { NewBadge } from '../NewBadge';
import { Rating } from '../Rating';

interface GiftListItemProps {
  mainImageUrl: string;
  title: string;
  donationAmount?: number;
  reviewRating: number;
  reviewCount: number;
  productNumber: string;
  /** 지자체 정보 */
  institution?: {
    id: number;
    logoUrl: string;
    name: string;
  };
  /** 성인전용 여부 */
  isAdult?: boolean;
  /** NEW 배지 표시 여부 */
  isNew?: boolean;
  /** 품절 여부 */
  isSoldOut?: boolean;
  onGiftLinkClick?: () => void;
  className?: string;
  /** 초기 로딩 우선순위 (LCP 최적화) */
  priority?: boolean;
}

export const GiftListItem = ({
  mainImageUrl,
  title,
  donationAmount,
  reviewRating,
  reviewCount,
  productNumber,
  institution,
  isAdult,
  isNew,
  isSoldOut,
  onGiftLinkClick,
  className,
  priority = false,
}: GiftListItemProps) => {
  return (
    <article className={cn('flex flex-col gap-8', className)}>
      <div className="relative shrink-0">
        {isNew ? (
          <div className="absolute left-0 top-0 z-[1]">
            <NewBadge />
          </div>
        ) : null}
        <CustomLink
          href={DONATION_ROUTES.GIFTS_DETAIL.pathname({ productNumber })}
          aria-label="답례품 상세 페이지로 이동"
          onClick={onGiftLinkClick}
        >
          <Image
            className="aspect-[1/1] w-full rounded-large object-cover"
            src={mainImageUrl || '/images/placeholder.png'}
            alt={title || '답례품 이미지'}
            width={400}
            height={400}
            priority={priority}
            sizes="(max-width: 768px) 50vw, 33vw"
          />
        </CustomLink>
        {isSoldOut ? (
          <div className="absolute inset-0 z-[1] rounded-large bg-Gray-black/40 flex-center">
            <span className="font-700 text-Gray-white typo-body2">
              품절되었어요
            </span>
          </div>
        ) : null}
        {isAdult ? (
          <div className="absolute bottom-12 right-12 z-10 rounded-xsmall bg-B-500 px-6 py-2 font-600 text-Gray-white typo-caption1">
            19세성인인증
          </div>
        ) : null}
      </div>
      <div className="grow">
        {institution && (
          <CustomLink
            href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
              id: institution.id,
            })}
            aria-label="지자체 상세 페이지로 이동"
            className="inline-block"
          >
            <div className="mb-4 flex items-center gap-4">
              <div className="relative size-20 overflow-hidden rounded-pill border border-Gray-300">
                <Image
                  className="object-cover"
                  src={institution.logoUrl || '/images/placeholder.png'}
                  alt={institution.name || '지자체 로고'}
                  width={20}
                  height={20}
                  sizes="20px"
                />
              </div>
              <p className="mt-1 font-600 text-Gray-600 typo-caption1">
                {institution.name}
              </p>
              <SvgChevronright className="size-12 text-Gray-600" />
            </div>
          </CustomLink>
        )}
        <CustomLink
          href={DONATION_ROUTES.GIFTS_DETAIL.pathname({ productNumber })}
          aria-label="답례품 상세 페이지로 이동"
          className="mb-4 line-clamp-2 w-fit overflow-hidden text-ellipsis font-500 text-Gray-900 typo-body4"
          onClick={onGiftLinkClick}
        >
          {title}
        </CustomLink>
        {donationAmount !== undefined ? (
          <p className="flex items-center gap-4 font-600 text-Gray-black typo-body3">
            <span>{donationAmount.toLocaleString()}원</span>
            <span className="text-Gray-550 typo-body4">기부시 증정</span>
          </p>
        ) : null}
        {reviewCount > 0 ? (
          <div className="flex flex-wrap gap-4 pt-4 font-400 text-Gray-600 typo-caption2">
            <Rating value={reviewRating} count={reviewCount} />
          </div>
        ) : null}
      </div>
    </article>
  );
};
